import { useMemo, useCallback } from 'react';
import { GenericShortListFilter } from '@hotjar/global-filters';
import { useTranslation } from '@hotjar/translations';
import { EmotionIcon, useEmotionLabels } from '@hotjar/webapp/features/Feedback/EmotionIcon';
import { RATING_TYPE } from '@hotjar/feedback/domain/Surveys/Responses/Filters/FilterDataType';
import { defaultRatingFilter } from '@hotjar/feedback/domain/Surveys/surveyViewStore';
import type { AbstractSingleFilterPillsProps, ListOption } from '@hotjar/global-filters';
import type { EmotionStyle } from '@hotjar/webapp/features/Feedback/domain/EmotionStyle';
import type { RatingFilterData } from '@hotjar/feedback/domain/Surveys/Responses/Filters/FilterData';
import type { RatingFilterValue } from '@hotjar/feedback/domain/Surveys/Responses/Filters/FilterDataType';
import type { EmotionScore } from '@hotjar/insights-common/constants/emotions';
import type { ChangeFilter, RemoveFilter } from './FilterTypes';

export interface RatingFilterProps extends AbstractSingleFilterPillsProps<RatingFilterValue> {
  emotionStyle: EmotionStyle;
  isDisabled?: boolean;
  filter: RatingFilterData;
}

export function ratingFilterOptions(emotionStyle: EmotionStyle): ListOption<RATING_TYPE>[] {
  return [
    {
      value: RATING_TYPE.LOVE,
      label: 'Love',
      content: <EmotionIcon emotionStyle={emotionStyle} score={4} size="s" />,
    },
    {
      value: RATING_TYPE.LIKE,
      label: 'Like',
      content: <EmotionIcon emotionStyle={emotionStyle} score={3} size="s" />,
    },
    {
      value: RATING_TYPE.NEUTRAL,
      label: 'Neutral',
      content: <EmotionIcon emotionStyle={emotionStyle} score={2} size="s" />,
    },
    {
      value: RATING_TYPE.DISLIKE,
      label: 'Dislike',
      content: <EmotionIcon emotionStyle={emotionStyle} score={1} size="s" />,
    },
    {
      value: RATING_TYPE.HATE,
      label: 'Hate',
      content: <EmotionIcon emotionStyle={emotionStyle} score={0} size="s" />,
    },
  ];
}

export const areAllRatingsIncluded = (ratings: RatingFilterValue): boolean =>
  ratings !== undefined &&
  ratings.includes(RATING_TYPE.HATE) &&
  ratings.includes(RATING_TYPE.DISLIKE) &&
  ratings.includes(RATING_TYPE.NEUTRAL) &&
  ratings.includes(RATING_TYPE.LIKE) &&
  ratings.includes(RATING_TYPE.LOVE);

export const RatingFilter = ({
  filter,
  onChange,
  onRemove,
  isDisabled,
  disabledReason,
  emotionStyle,
}: {
  filter: RatingFilterData;
  onChange: ChangeFilter<RatingFilterData>;
  onRemove: RemoveFilter<RatingFilterData>;
  isDisabled?: boolean;
  disabledReason?: string;
  emotionStyle: EmotionStyle;
}): JSX.Element => {
  const { t } = useTranslation('feedback', { keyPrefix: 'survey-response.filters.rating' });
  const emotionLabels = useEmotionLabels();

  const currentValue = useMemo((): RatingFilterValue => {
    if (filter.value === undefined) return defaultRatingFilter;

    return filter.value;
  }, [filter]);

  const options = ratingFilterOptions(emotionStyle).map((option) => ({
    ...option,
    label: emotionLabels[(option.value - 1) as EmotionScore],
  }));

  function getSelectedOptionsLabel(ratings: RATING_TYPE[] | undefined): string {
    if (ratings === undefined || areAllRatingsIncluded(ratings)) {
      return '';
    }
    if (ratings.length > 2) {
      return `(${ratings.length})`;
    }

    return `${t('is')} ${ratings
      .map((rating) => emotionLabels[(rating - 1) as EmotionScore])
      .join(` ${t('or')} `)}`;
  }

  const onApply = useCallback(
    (newValue: RatingFilterValue) => {
      if (areAllRatingsIncluded(newValue)) {
        onChange(filter, undefined);
        return;
      }
      onChange(filter, newValue);
    },
    [onChange, filter],
  );

  const onRemoveFilter = useCallback(() => {
    onRemove(filter);
  }, [onRemove, filter]);

  return (
    <GenericShortListFilter
      filterName={t('label')}
      iconName="like"
      currentValue={currentValue}
      onApply={onApply}
      onRemove={!areAllRatingsIncluded(currentValue) ? onRemoveFilter : undefined}
      optionList={options}
      label1={t('label')}
      label2={getSelectedOptionsLabel(currentValue)}
      showDropdownCaret
      isInactive={!currentValue || areAllRatingsIncluded(currentValue)}
      selectAllLabel={t('all-ratings')}
      isDisabled={isDisabled}
      disabledReason={disabledReason}
    />
  );
};
